import React, { useEffect, useRef, useState } from 'react';
import { Spin, Button, Alert } from 'antd';
import { DownloadOutlined, FilePdfOutlined, NumberOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { apiUrls } from 'config';
import { API } from 'utils';
import { decryptId } from 'utils/crypto';
import BannerWithImage from 'components/BannerWithImage';
import styles from './ReportePDF.module.less';

interface ReporteConfig {
	titulo: string;
	etiquetaBadge: string;
	prefijoArchivo: string;
}

const DEFAULT_CONFIG: ReporteConfig = {
	titulo: 'Reporte',
	etiquetaBadge: 'Documento #',
	prefijoArchivo: 'reporte',
};

const ReportePDF = () => {
	const { id, token } = useParams<Record<string, string | undefined>>();

	const [config, setConfig] = useState<ReporteConfig>(DEFAULT_CONFIG);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [isMobile, setIsMobile] = useState(false);

	const objectUrlRef = useRef<string | null>(null);

	// Solo para mostrar el numero en el badge / nombre de archivo.
	// La generacion del PDF la valida y resuelve el backend.
	const decodedId = token ? decryptId(token) : null;

	useEffect(() => {
		const updateIsMobile = () => {
			const userAgent = navigator.userAgent || navigator.vendor;
			setIsMobile(/android|iphone|ipad|mobile/i.test(userAgent) || window.innerWidth < 768);
		};

		updateIsMobile();
		window.addEventListener('resize', updateIsMobile);

		return () => window.removeEventListener('resize', updateIsMobile);
	}, []);

	useEffect(() => {
		if (!id) return;

		const cargarConfig = async () => {
			try {
				const response = await API.getReporteConfig(id);
				if (response.status === 200 && response.data) {
					setConfig({ ...DEFAULT_CONFIG, ...response.data });
				}
			} catch (error) {
				console.error('No se pudo cargar la configuracion del reporte:', error);
			}
		};

		cargarConfig();
	}, [id]);

	useEffect(() => {
		if (!id || !token) {
			setErrorMsg('El enlace es invalido o ha expirado.');
			setLoading(false);
			return;
		}

		const fetchPdf = async () => {
			try {
				const response = await API.getReportePdf(id, token);

				if (response.status !== 200) {
					throw new Error('Error al generar el reporte');
				}

				const blob = response.data as Blob;
				const url = URL.createObjectURL(blob);
				objectUrlRef.current = url;
				setPdfUrl(url);
			} catch (error) {
				console.error('Error PDF:', error);
				setErrorMsg('No se pudo cargar el reporte.');
			} finally {
				setLoading(false);
			}
		};

		fetchPdf();

		return () => {
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
			}
		};
	}, [id, token]);

	const handleDownload = () => {
		if (!pdfUrl) return;

		const link = document.createElement('a');
		link.href = pdfUrl;
		link.download = `${config.prefijoArchivo}-${decodedId ?? id}.pdf`;
		link.click();
	};

	const getDirectPdfUrl = () => {
		if (!id || !token) return pdfUrl;

		const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
		return `${baseUrl}${apiUrls.reportePdf}/${id}/${token}`;
	};

	useEffect(() => {
		if (!isMobile || !pdfUrl) return;

		const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
		const directUrl = id && token
			? `${baseUrl}${apiUrls.reportePdf}/${id}/${token}`
			: pdfUrl;
		if (!directUrl) return;

		const timer = setTimeout(() => {
			window.location.replace(directUrl);
		}, 300);

		return () => clearTimeout(timer);
	}, [isMobile, pdfUrl, id, token]);

	const handleOpenPdf = () => {
		const url = getDirectPdfUrl();
		if (!url) return;

		window.location.href = url;
	};

	return (
		<section className={styles.pageWrapper}>
			<BannerWithImage>
				<div className={styles.contentWrapper}>

					<div className={styles.pageHeader}>
						<h4>
							<FilePdfOutlined style={{ marginRight: 10 }} />
							{config.titulo}
						</h4>
					</div>
					{decodedId && (
						<div className={styles.idBadge}>
							<NumberOutlined /> {config.etiquetaBadge} {decodedId}
						</div>
					)}
					<div className={styles.card}>

						{loading && (
							<div className={styles.loadingWrapper}>
								<Spin size="large" />
								<p>Cargando reporte...</p>
							</div>
						)}

						{errorMsg && !loading && (
							<Alert type="error" message="Error" description={errorMsg} />
						)}

						{pdfUrl && !loading && (
							<>
								<div className={styles.toolbar}>
									<Button
										type="primary"
										icon={<DownloadOutlined />}
										onClick={handleDownload}
										block={isMobile}
									>
										Descargar PDF
									</Button>
									{isMobile && (
										<Button
											icon={<FilePdfOutlined />}
											onClick={handleOpenPdf}
											block
										>
											Abrir PDF
										</Button>
									)}
								</div>

								{!isMobile ? (
									<div className={styles.iframeWrapper}>
										<object
											data={pdfUrl}
											type="application/pdf"
											width="100%"
											height="100%"
											style={{ minHeight: '75vh' }}
										/>
									</div>
								) : (
									<div className={styles.mobilePdfViewer}>
										<Spin />
										<p>Abriendo vista previa del PDF...</p>
										<p>Si no abre automaticamente, toca el boton.</p>
										<Button
											type="primary"
											icon={<FilePdfOutlined />}
											onClick={handleOpenPdf}
											block
										>
											Tocar para abrir PDF
										</Button>
									</div>
								)}
							</>
						)}

					</div>
				</div>
			</BannerWithImage>
		</section>
	);
};

export default ReportePDF;
